import React, { useMemo, useCallback } from 'react';
import { PersonnelConfig, Service, Competence } from '../../../../../../shared/types/config';

interface PersonnelTabProps {
  personnelConfigs: PersonnelConfig[];
  searchPersonnel: string;
  onSearchChange: (value: string) => void;
  filterService: number | 'all';
  onFilterServiceChange: (value: number | 'all') => void;
  onAdd: () => void;
  onEdit: (personnel: PersonnelConfig) => void;
  onDelete: (id: number) => void;
  services: Service[];
  competences: Competence[];
}

function sortPersonnelByGradeRank(a: PersonnelConfig, b: PersonnelConfig): number {
  const oa = a.ordre ?? 0;
  const ob = b.ordre ?? 0;
  if (oa !== ob) return oa - ob;
  return a.grade.localeCompare(b.grade, 'fr', { sensitivity: 'base' });
}

const PersonnelTab: React.FC<PersonnelTabProps> = ({
  personnelConfigs,
  searchPersonnel,
  onSearchChange,
  filterService,
  onFilterServiceChange,
  onAdd,
  onEdit,
  onDelete,
  services,
  competences,
}) => {
  const matchesSearch = useCallback(
    (p: PersonnelConfig) => {
      const searchLower = searchPersonnel.toLowerCase();
      if (!searchLower) return true;
      if (p.grade.toLowerCase().includes(searchLower)) return true;
      if (p.id_competences?.length) {
        return p.id_competences.some((id) => {
          const competence = competences.find((c) => c.id === id);
          return competence && competence.nom.toLowerCase().includes(searchLower);
        });
      }
      return false;
    },
    [searchPersonnel, competences]
  );

  const groupedByService = useMemo(() => {
    const map = new Map<number, PersonnelConfig[]>();
    for (const p of personnelConfigs) {
      if (!matchesSearch(p)) continue;
      const sid = p.id_service;
      if (!map.has(sid)) map.set(sid, []);
      map.get(sid)!.push(p);
    }
    Array.from(map.values()).forEach((list) => list.sort(sortPersonnelByGradeRank));
    const orderedIds: number[] = [];
    for (const s of services) {
      if (map.has(s.id)) orderedIds.push(s.id);
    }
    Array.from(map.keys()).forEach((sid) => {
      if (!orderedIds.includes(sid)) orderedIds.push(sid);
    });
    return { map, orderedIds };
  }, [personnelConfigs, services, matchesSearch]);

  const sections: { serviceId: number; label: string; rows: PersonnelConfig[] }[] = useMemo(() => {
    if (filterService === 'all') {
      return groupedByService.orderedIds.map((serviceId) => ({
        serviceId,
        label: services.find((s) => s.id === serviceId)?.nom ?? `Service #${serviceId}`,
        rows: groupedByService.map.get(serviceId) ?? [],
      }));
    }
    const label = services.find((s) => s.id === filterService)?.nom ?? `Service #${filterService}`;
    const rows = (groupedByService.map.get(filterService) ?? []).filter((p) => p.id_service === filterService);
    return [{ serviceId: filterService, label, rows }];
  }, [filterService, groupedByService, services]);

  const showServiceColumn = filterService === 'all';

  const renderRow = (personnel: PersonnelConfig) => {
    const service = services.find((s) => s.id === personnel.id_service);
    const competencesNames =
      personnel.id_competences?.map((id) => competences.find((c) => c.id === id)?.nom).filter(Boolean) || [];
    return (
      <tr key={personnel.id} className="border-b border-gray-700 hover:bg-gray-800/50">
        <td className="px-4 py-3 text-gray-300">{personnel.id}</td>
        <td className="px-4 py-3 text-gray-400 tabular-nums">{personnel.ordre ?? 0}</td>
        <td className="px-4 py-3 text-white font-medium">{personnel.grade}</td>
        <td className="px-4 py-3 text-gray-300">{personnel.prix.toLocaleString()} €</td>
        <td className="px-4 py-3">
          {competencesNames.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {competencesNames.map((nom, index) => (
                <span
                  key={index}
                  className="px-2 py-1 bg-blue-500/20 text-blue-300 rounded text-xs border border-blue-500/50"
                >
                  {nom}
                </span>
              ))}
            </div>
          ) : (
            <span className="text-gray-500 text-xs">Aucune</span>
          )}
        </td>
        {showServiceColumn && (
          <td className="px-4 py-3 text-gray-300">{service?.nom || personnel.id_service}</td>
        )}
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            <button
              onClick={() => onEdit(personnel)}
              className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
              title="Modifier"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
            </button>
            <button
              onClick={() => personnel.id && onDelete(personnel.id)}
              className="p-2 bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
              title="Supprimer"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </button>
          </div>
        </td>
      </tr>
    );
  };

  const totalRows = sections.reduce((n, s) => n + s.rows.length, 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-1 flex-col gap-3 sm:flex-row sm:items-center">
          <div className="sm:w-56">
            <label className="block text-xs text-gray-400 mb-1">Service</label>
            <select
              value={filterService === 'all' ? 'all' : String(filterService)}
              onChange={(e) => {
                const v = e.target.value;
                onFilterServiceChange(v === 'all' ? 'all' : parseInt(v, 10));
              }}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Tous (groupés par service)</option>
              {services.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nom}
                </option>
              ))}
            </select>
          </div>
          <div className="flex-1 relative min-w-0">
            <label className="block text-xs text-gray-400 mb-1">Recherche grade / compétence</label>
            <input
              type="text"
              placeholder="Grade ou libellé de compétence…"
              value={searchPersonnel}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <svg
              className="w-5 h-5 absolute right-3 top-8 text-gray-400 pointer-events-none"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>
        <button
          onClick={onAdd}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 shrink-0"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Ajouter
        </button>
      </div>
      <p className="text-xs text-gray-500">
        Les grades sont triés par <strong className="text-gray-400">rang</strong> puis par nom, séparément pour chaque
        service. Un service ne voit que ses propres lignes lorsque vous filtrez sur ce service.
      </p>

      <div className="overflow-x-auto space-y-8">
        {totalRows === 0 ? (
          <div className="bg-gray-800/30 border border-gray-700 rounded-lg px-4 py-8 text-center text-gray-400">
            Aucun personnel pour ce filtre ou cette recherche
          </div>
        ) : (
          sections.map(
            ({ serviceId, label, rows }) =>
              rows.length > 0 && (
                <div key={serviceId}>
                  {filterService === 'all' && (
                    <h3 className="text-sm font-semibold text-gray-200 mb-2 pb-2 border-b border-gray-600">{label}</h3>
                  )}
                  <table className="w-full text-left">
                    <thead className="bg-gray-800 border-b border-gray-700">
                      <tr>
                        <th className="px-4 py-3 text-gray-300 font-semibold">ID</th>
                        <th className="px-4 py-3 text-gray-300 font-semibold">Rang</th>
                        <th className="px-4 py-3 text-gray-300 font-semibold">Grade</th>
                        <th className="px-4 py-3 text-gray-300 font-semibold">Prix</th>
                        <th className="px-4 py-3 text-gray-300 font-semibold">Compétences</th>
                        {showServiceColumn && (
                          <th className="px-4 py-3 text-gray-300 font-semibold">Service</th>
                        )}
                        <th className="px-4 py-3 text-gray-300 font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map(renderRow)}
                    </tbody>
                  </table>
                </div>
              )
          )
        )}
      </div>
    </div>
  );
};

export default PersonnelTab;
